import React, { useMemo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems?: number;
  itemsPerPage?: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (size: number) => void;
}

type PageItem = number | 'ellipsis';

function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);

  // Near the start: show a few more early pages.
  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  // Near the end: show a few more late pages.
  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
  }

  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const out: PageItem[] = [];
  let prev: number | null = null;

  for (const p of sorted) {
    if (prev !== null && p - prev > 1) {
      out.push('ellipsis');
    }
    out.push(p);
    prev = p;
  }

  return out;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage = 10,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onItemsPerPageChange,
}) => {
  const pageItems = useMemo(() => getPageItems(currentPage, totalPages), [currentPage, totalPages]);

  if (totalPages <= 1 && !onItemsPerPageChange) {
    return null;
  }

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-2">
        <button
          className="px-3 py-1 rounded border border-gray-200 disabled:opacity-50"
          onClick={() => canGoPrev && onPageChange(currentPage - 1)}
          disabled={!canGoPrev}
        >
          Previous
        </button>

        <div className="flex items-center gap-1 flex-wrap">
          {pageItems.map((item, idx) => {
            if (item === 'ellipsis') {
              return (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-500 select-none">
                  …
                </span>
              );
            }

            const page = item;
            const active = currentPage === page;

            return (
              <button
                key={page}
                className={`px-3 py-1 rounded border ${
                  active ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-700'
                }`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          className="px-3 py-1 rounded border border-gray-200 disabled:opacity-50"
          onClick={() => canGoNext && onPageChange(currentPage + 1)}
          disabled={!canGoNext}
        >
          Next
        </button>
      </div>

      <div className="flex items-center gap-4 text-sm text-gray-600">
        {typeof totalItems === 'number' && (
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
          </span>
        )}

        {onItemsPerPageChange && (
          <label className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={(event) => {
                const { value } = event.target;
                onItemsPerPageChange(Number(value));
              }}
              className="border rounded px-2 py-1"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
};
